import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Mail, Phone, User } from 'lucide-react';
import { FUNDGENIUS_HQ_PRODUCT_NAME } from '../../utils/funding/constants';
import { getFundGeniusAppUrl } from '../../utils/hostnamePortal';
import { FUNDGENIUS_DOMAINS } from '../../config/fundgeniusDomains';

/**
 * Public FundGenius intake portal.
 * Supports:
 *   intake.fundgeniushq.com
 *   intake.fundgeniushq.com/{agencySlug}
 *   intake.fundgeniushq.com/{agencySlug}/start
 *   intake.fundgeniushq.com/{agencySlug}/client/{clientInviteId}
 */
const FundingIntakePortalPage = () => {
  const { agencySlug, clientInviteId } = useParams();
  const [submitted, setSubmitted] = useState(false);

  const branding = useMemo(() => ({
    title: agencySlug
      ? `${agencySlug.replace(/[-_]/g, ' ')} funding intake`
      : `${FUNDGENIUS_HQ_PRODUCT_NAME} intake`,
    subtitle: agencySlug
      ? `Submitting into workspace “${agencySlug}”`
      : `Client intake portal · ${FUNDGENIUS_DOMAINS.INTAKE_DOMAIN}`,
  }), [agencySlug]);

  const handleSubmit = (event) => {
    event.preventDefault();
    // Phase 1: accept intake locally. Wired agency submission lands with funding clients API.
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#f4f7fb] text-slate-900" data-testid="funding-intake-portal">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between px-6 py-4">
          <div>
            <div className="text-lg font-semibold">{FUNDGENIUS_HQ_PRODUCT_NAME}</div>
            <div className="text-sm text-slate-500">{branding.subtitle}</div>
          </div>
          <a
            href={getFundGeniusAppUrl('/login')}
            className="text-sm font-medium text-cyan-700 hover:text-cyan-800"
          >
            Agency login
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-3xl font-bold tracking-tight">{branding.title}</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Share your business details to start the FundGenius intake process.
            {clientInviteId ? ` Invite reference: ${clientInviteId}.` : ''}
            {' '}Your agency team reviews submissions inside FundGenius HQ.
          </p>

          {submitted ? (
            <div
              className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800"
              data-testid="funding-intake-submitted"
            >
              Intake received
              {agencySlug ? ` for ${agencySlug}` : ''}.
              A funding advisor can continue this record in the app portal.
            </div>
          ) : (
            <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-testid="funding-intake-form">
              <input type="hidden" name="agency_slug" value={agencySlug || ''} />
              <input type="hidden" name="client_invite_id" value={clientInviteId || ''} />

              <label className="block">
                <span className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Building2 className="h-4 w-4" />
                  Business name
                </span>
                <input
                  required
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none ring-cyan-500 focus:ring-2"
                  placeholder="Acme Holdings LLC"
                />
              </label>

              <label className="block">
                <span className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                  <User className="h-4 w-4" />
                  Contact name
                </span>
                <input
                  required
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none ring-cyan-500 focus:ring-2"
                  placeholder="Jordan Lee"
                />
              </label>

              <div className="grid gap-5 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Mail className="h-4 w-4" />
                    Email
                  </span>
                  <input
                    required
                    type="email"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none ring-cyan-500 focus:ring-2"
                    placeholder="you@company.com"
                  />
                </label>

                <label className="block">
                  <span className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Phone className="h-4 w-4" />
                    Phone
                  </span>
                  <input
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none ring-cyan-500 focus:ring-2"
                    placeholder="(555) 555-0100"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Funding goal</span>
                <textarea
                  required
                  rows={4}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none ring-cyan-500 focus:ring-2"
                  placeholder="Describe the funding amount, use of funds, and timeline."
                />
              </label>

              <button
                type="submit"
                className="rounded-full bg-cyan-600 px-5 py-3 text-sm font-semibold text-white hover:bg-cyan-700"
              >
                Submit intake
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default FundingIntakePortalPage;
